import { useEffect } from "react";
import { MapPin, Phone, Clock, MessageCircle, Navigation, ChevronDown, CalendarCheck } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import { Reveal, SectionTitle } from "@/components/site/Reveal";
import { Reviews } from "@/components/site/Sections";
import { usePageMeta } from "@/components/site/usePageMeta";
import { useLang } from "@/i18n/LangContext";
import { LANDINGS } from "@/data/landing";
import { SERVICES } from "@/data/site";
import { EMAIL, waLink } from "@/lib/api";
import { NextDayButton } from "@/components/site/NextDayButton";
import { OfficeHoursBadge } from "@/components/site/OfficeHoursBadge";

const useLocalSchema = (c, lang) => {
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify([
      { "@context": "https://schema.org", "@type": "Dentist", name: c.clinic, url: window.location.origin + c.path, image: window.location.origin + c.img, telephone: c.phone, email: EMAIL, address: { "@type": "PostalAddress", streetAddress: c.address, addressLocality: c.city, addressCountry: "NI" }, ...(c.geo ? { geo: { "@type": "GeoCoordinates", ...c.geo } } : {}), ...(c.openingHours ? { openingHours: c.openingHours } : {}), founder: { "@type": "Person", name: "Dra. Cora Baltodano", jobTitle: "DDS, Orthodontics Specialist" }, medicalSpecialty: "Dentistry" },
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faqs.map((f) => ({ "@type": "Question", name: lang === "es" ? f.q_es : f.q, acceptedAnswer: { "@type": "Answer", text: lang === "es" ? f.a_es : f.a } })) },
    ]);
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, [c, lang]);
};

const Intro = ({ c }) => {
  const { tr, pick } = useLang();
  return (
    <section className="pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal from="left" className="lg:col-span-5"><img src={c.img} alt={tr(`Dra. Cora Baltodano, dentist in ${c.city}`, `Dra. Cora Baltodano, dentista en ${c.city}`)} className="w-full rounded-[2rem] object-cover aspect-[4/5]" data-testid="landing-intro-img" /></Reveal>
        <Reveal from="right" delay={0.15} className="lg:col-span-7 space-y-6">
          <h2 className="font-display uppercase text-3xl sm:text-4xl">{tr(`Your trusted dentist in ${c.city}`, `Tu dentista de confianza en ${c.city}`)}</h2>
          <p className="text-lg text-slate-300 leading-relaxed">{pick(c, "intro")}</p>
          <OfficeHoursBadge office={c.key} />
          <div className="space-y-3 text-slate-300">
            <p className="flex gap-3"><MapPin className="text-[#00C9B7] shrink-0" size={18} /> {pick(c, "address")}</p>
            <a href={c.tel} className="flex gap-3 hover:text-white"><Phone className="text-[#00C9B7]" size={18} /> {c.phone}</a>
            <div className="flex gap-3"><Clock className="text-[#00C9B7] shrink-0" size={18} /><div>{pick(c, "hours").map((h) => <p key={h}>{h}</p>)}</div></div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={waLink(tr(`Hello! I'd like an appointment in ${c.city}.`, `¡Hola! Quisiera una cita en ${c.city}.`), c.wa)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 text-xs font-bold uppercase tracking-widest" data-testid="landing-whatsapp-btn"><MessageCircle size={16} /> WhatsApp {c.waDisplay}</a>
            {c.key === "sjds" && <NextDayButton testId="landing-next-day-btn" />}
            {c.key === "managua" && <a href={waLink(tr("Hello Dr. Cora! I'd like an appointment this week at the Managua office. What days and times are available?", "¡Hola Dra. Cora! Quisiera una cita esta semana en la clínica de Managua. ¿Qué días y horarios tienen disponibles?"), c.wa)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#00C9B7] text-[#00C9B7] px-6 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#00C9B7] hover:text-[#0B0F12] transition-colors" data-testid="landing-this-week-btn"><CalendarCheck size={16} /> {tr("Book an appointment this week", "Agendar cita esta semana")}</a>}
            <a href={c.directions} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors" data-testid="landing-directions-btn"><Navigation size={16} /> {tr("Get directions", "Cómo llegar")}</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const LocalServices = ({ c }) => {
  const { tr, pick } = useLang();
  return (
    <section className="py-20 sm:py-28 bg-[#080B0D]" data-testid="landing-services">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionTitle eyebrow={tr("Services", "Servicios")} title={tr(`Dental services in ${c.city}`, `Servicios dentales en ${c.city}`)} className="mb-12" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08} className="rounded-2xl bg-[#131B21] border border-[#00C9B7]/15 p-6 hover:border-[#00C9B7]/50 transition-colors">
              <s.icon className="text-[#00C9B7] mb-4" size={22} />
              <h3 className="font-display uppercase text-xl mb-2">{pick(s, "title")} {tr("in", "en")} {c.city}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{pick(s, "desc")}</p>
              <a href={waLink(tr(`Hello! I'd like information about ${s.title} in ${c.city}.`, `¡Hola! Quisiera información sobre ${pick(s, "title")} en ${c.city}.`), c.wa)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#25D366] hover:text-white" data-testid={`landing-service-ask-${i}`}><MessageCircle size={14} /> {tr("Ask about this", "Preguntar")}</a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Faq = ({ c }) => {
  const { tr } = useLang();
  return (
    <section className="py-20 sm:py-28" data-testid="landing-faq">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <SectionTitle eyebrow="FAQ" title={tr(`Questions about our ${c.city} clinic`, `Preguntas sobre nuestra clínica en ${c.city}`)} className="mb-10" />
        <div className="space-y-3">
          {c.faqs.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.06}>
              <details className="group rounded-2xl bg-[#131B21] border border-white/5 p-6 open:border-[#00C9B7]/40" data-testid={`landing-faq-${i}`}>
                <summary className="flex justify-between items-center cursor-pointer list-none font-semibold">{tr(f.q, f.q_es)}<ChevronDown className="text-[#00C9B7] group-open:rotate-180 transition-transform" size={18} /></summary>
                <p className="mt-4 text-slate-300 leading-relaxed">{tr(f.a, f.a_es)}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const MapAndForm = ({ c }) => {
  const { tr } = useLang();
  return (
    <section className="pb-24 sm:pb-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
        <Reveal from="left" className="rounded-3xl overflow-hidden border border-[#00C9B7]/15 min-h-[360px]">
          <iframe title={`${c.clinic} map`} src={c.map} className="w-full h-full min-h-[360px] grayscale-[60%] invert-[90%] hue-rotate-180" loading="lazy" />
        </Reveal>
        <Reveal from="right" delay={0.15} className="rounded-3xl bg-[#131B21] border border-[#00C9B7]/15 p-6 sm:p-10">
          <p className="font-display uppercase text-2xl mb-6">{tr(`Book in ${c.city}`, `Agenda en ${c.city}`)}</p>
          <LeadForm source={`landing-${c.key}`} location={c.city} idPrefix={`landing-${c.key}`} />
        </Reveal>
      </div>
    </section>
  );
};

export default function LocalDentist({ id }) {
  const c = LANDINGS[id];
  const { tr, pick, lang } = useLang();
  usePageMeta(pick(c, "title").split(" | ")[0], pick(c, "desc"), pick(c, "keywords"), c.img);
  useLocalSchema(c, lang);
  return (
    <div data-testid={`landing-${c.key}`}>
      <PageHero eyebrow={tr(`Dentist in ${c.city}, Nicaragua`, `Dentista en ${c.city}, Nicaragua`)} title={tr(<>Looking for a dentist in <span className="text-[#00C9B7]">{c.city}?</span></>, <>¿Buscas dentista en <span className="text-[#00C9B7]">{c.city}?</span></>)} img={c.heroImg}>
        <p className="text-lg text-slate-300 max-w-2xl">{tr("Dr. Cora Baltodano and our team are happy to help.", "La Dra. Cora Baltodano y nuestro equipo están felices de ayudarte.")}</p>
      </PageHero>
      {c.key === "managua" && (
        <Reveal from="zoom" className="max-w-7xl mx-auto px-5 sm:px-8 pb-16"><img src={c.banner} alt="Nuestra nueva sucursal en Managua – SunCare Dental" className="w-full rounded-[2rem]" data-testid="landing-banner-img" /></Reveal>
      )}
      <Intro c={c} />
      {c.video && (
        <section className="pb-20 sm:pb-28" data-testid="landing-video-section">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <SectionTitle eyebrow={tr("Video", "Video")} title={tr(`Inside our ${c.city} clinic`, `Conoce nuestra clínica en ${c.city}`)} className="mb-8" />
            <Reveal from="up">
              <video src={c.video} controls muted autoPlay loop playsInline preload="metadata" className="w-full max-h-[80vh] rounded-[2rem] bg-black border border-[#00C9B7]/20" data-testid="landing-video" />
            </Reveal>
          </div>
        </section>
      )}
      <LocalServices c={c} />
      <Reviews />
      <Faq c={c} />
      <MapAndForm c={c} />
    </div>
  );
}
